import React from "react";
import { Box, Container, Grid, Typography, Stack, IconButton, Divider, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

const accent = "#0d47a1";
const accentLight = "#e3f2fd";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

const platformLinks = [
  { label: "Client Login", to: "/client-login" },
  { label: "Client Sign Up", to: "/client-signup" },
  { label: "Request Demo", to: "/contact" },
];

const serviceLinks = [
  { label: "Restaurant Solutions", to: "/services" },
  { label: "Healthcare Solutions", to: "/services" },
  { label: "AI Voice Agents", to: "/services" },
  { label: "POS Integration", to: "/services" },
];

const socials = [
  { icon: <FacebookIcon />, href: "#", label: "Facebook" },
  { icon: <LinkedInIcon />, href: "#", label: "LinkedIn" },
  { icon: <TwitterIcon />, href: "#", label: "Twitter" },
  { icon: <InstagramIcon />, href: "#", label: "Instagram" },
];

const Footer = () => (
  <Box component="footer" sx={{ background: `linear-gradient(180deg, #0a1929 0%, ${accent} 150%)`, color: "#fff", pt: { xs: 6, md: 8 }, pb: 3 }}>
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        {/* Brand */}
        <Grid item xs={12} md={4}>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5 }}>
            Rite<span style={{ color: accentLight }}>.</span>
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, lineHeight: 1.8, maxWidth: 320, mb: 2 }}>
            Zero-commission SaaS platform powering marketing automation for restaurants, healthcare, and beyond. Built in India, designed for the world.
          </Typography>
          <Stack direction="row" spacing={1}>
            {socials.map((s, i) => (
              <IconButton key={i} href={s.href} aria-label={s.label} size="small"
                sx={{ color: "#fff", opacity: 0.7, border: "1px solid rgba(255,255,255,0.15)",
                  "&:hover": { opacity: 1, background: "rgba(255,255,255,0.1)" } }}>
                {s.icon}
              </IconButton>
            ))}
          </Stack>
        </Grid>

        {/* Quick Links */}
        <Grid item xs={6} sm={4} md={2}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: accentLight, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1.5 }}>
            Quick Links
          </Typography>
          <Stack spacing={1}>
            {quickLinks.map((l, i) => (
              <MuiLink key={i} component={Link} to={l.to} underline="none"
                sx={{ color: "#fff", opacity: 0.7, fontSize: "0.85rem",
                  "&:hover": { opacity: 1, color: accentLight } }}>
                {l.label}
              </MuiLink>
            ))}
          </Stack>
        </Grid>

        {/* Services */}
        <Grid item xs={6} sm={4} md={3}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: accentLight, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1.5 }}>
            Solutions
          </Typography>
          <Stack spacing={1}>
            {serviceLinks.map((l, i) => (
              <MuiLink key={i} component={Link} to={l.to} underline="none"
                sx={{ color: "#fff", opacity: 0.7, fontSize: "0.85rem",
                  "&:hover": { opacity: 1, color: accentLight } }}>
                {l.label}
              </MuiLink>
            ))}
          </Stack>
        </Grid>

        {/* Platform & Contact */}
        <Grid item xs={12} sm={4} md={3}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: accentLight, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1.5 }}>
            Platform
          </Typography>
          <Stack spacing={1} sx={{ mb: 3 }}>
            {platformLinks.map((l, i) => (
              <MuiLink key={i} component={Link} to={l.to} underline="none"
                sx={{ color: "#fff", opacity: 0.7, fontSize: "0.85rem",
                  "&:hover": { opacity: 1, color: accentLight } }}>
                {l.label}
              </MuiLink>
            ))}
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              <MuiLink href="mailto:support@riteautomation.com" underline="none"
                sx={{ color: "#fff", opacity: 0.7, fontSize: "0.85rem", "&:hover": { opacity: 1 } }}>
                support@riteautomation.com
              </MuiLink>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              <MuiLink href="tel:+15551234567" underline="none"
                sx={{ color: "#fff", opacity: 0.7, fontSize: "0.85rem", "&:hover": { opacity: 1 } }}>
                +1 (555) 123-4567
              </MuiLink>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 4 }} />

      {/* Bottom bar */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{ opacity: 0.5 }}>
          © {new Date().getFullYear()} Rite Marketing Automation. All rights reserved.
        </Typography>
        <Stack direction="row" spacing={2}>
          {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((t, i) => (
            <MuiLink key={i} href="#" underline="none"
              sx={{ color: "#fff", opacity: 0.5, fontSize: "0.7rem", "&:hover": { opacity: 0.8 } }}>
              {t}
            </MuiLink>
          ))}
        </Stack>
      </Stack>
    </Container>
  </Box>
);

export default Footer;
